import numeral from 'numeral';

import type { Importer } from '@feathr/blackbox';

/** Whether the import is complete or failed. */
export function hasRunFinished(importer: Importer): boolean {
  const state = importer.get('state');
  return state === 'complete' || state === 'failed';
}

/** How many rows the run wrote: the people it created plus the people it updated. */
export function getImportedRowCount(importer: Importer): number {
  return importer.get('inserted', 0) + importer.get('duplicates', 0);
}

/** Whether a complete import imported some rows and skipped others. */
function isCompleteWithSkippedRows(importer: Importer): boolean {
  return (
    importer.get('state') === 'complete' &&
    getImportedRowCount(importer) > 0 &&
    importer.get('skipped', 0) > 0
  );
}

/**
 * The row counts of a run that failed partway, or that completed with some rows imported and some
 * skipped. `skipped` includes the rows a failed run never reached, so imported plus skipped is
 * every row it read.
 */
export function getPartialImportCounts(
  importer: Importer,
): { imported: number; skipped: number; total: number } | undefined {
  if (importer.get('state') !== 'failed' && !isCompleteWithSkippedRows(importer)) {
    return undefined;
  }
  const imported = getImportedRowCount(importer);
  const skipped = importer.get('skipped', 0);
  const total = imported + skipped;
  return total > 0 ? { imported, skipped, total } : undefined;
}

/** A count with thousands separators, for the run lines' sentences. */
export function formatImportCount(count: number): string {
  return numeral(count).format('0,0');
}
